import { CircleCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import CtaSection from "@/components/CtaSection";
import { STEPS } from "@/data/content";

export default function HowItWorks() {
  return (
    <>
      <SEO
        title="How Medical Travel to India Works | GetWellSoon"
        description="From sharing medical reports to on-ground support in India — see the five-step coordination process GetWellSoon manages for international patients and families."
      />

      <section className="bg-pearl" data-testid="how-it-works-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Chapter index="01" label="How it works" />
            <MaskedLines
              delay={0.1}
              className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              lines={[
                "From first report",
                <span key="l2">
                  to <span className="italic text-brand">safe return</span> home.
                </span>,
              ]}
            />
            <FadeIn delay={0.5}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Five steps, one coordinator, and no guesswork. Here is exactly
                how we organize treatment travel to India — and what we handle
                at every stage.
              </p>
            </FadeIn>
          </div>
          <div className="relative lg:col-span-5">
            <FadeIn delay={0.3}>
              <div className="overflow-hidden rounded-[2rem] rounded-tr-[5rem] border border-border">
                <img
                  src="/images/consult-tablet.jpg"
                  alt="Medical reports being prepared securely online"
                  className="h-[300px] w-full object-cover md:h-[380px]"
                  data-testid="how-it-works-image"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="how-it-works-steps">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="relative">
            <div className="absolute bottom-0 left-6 top-0 w-px bg-border md:left-1/2" />
            {STEPS.map((s, i) => (
              <FadeIn
                key={s.title}
                className={`relative md:grid md:grid-cols-2 md:gap-20 ${i > 0 ? "mt-16 md:mt-24" : ""}`}
                data-testid={`how-step-${i + 1}`}
              >
                <div
                  className={`absolute left-6 top-1 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-white font-serif text-lg italic text-brand md:left-1/2`}
                >
                  {i + 1}
                </div>
                <div
                  className={`pl-16 md:pl-0 ${
                    i % 2 === 1 ? "md:col-start-2" : "md:pr-4 md:text-right"
                  }`}
                >
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-light text-sage-dark`}
                  >
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-5 font-serif text-2xl tracking-tight text-ink md:text-3xl">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    {s.description}
                  </p>
                  <ul
                    className={`mt-6 space-y-3 ${
                      i % 2 === 0 ? "md:flex md:flex-col md:items-end" : ""
                    }`}
                  >
                    {s.points.map((pt) => (
                      <li
                        key={pt}
                        className={`flex items-start gap-3 text-sm leading-relaxed text-ink/80 ${
                          i % 2 === 0 ? "md:flex-row-reverse md:text-right" : ""
                        }`}
                      >
                        <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-dark" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
